import { useActiveOrganization } from '@/features/organization/api/get-active-organization';
import { queryKeys } from '@/lib/query-keys';
import { useSession } from '@/lib/session';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type IcalCalendarSettings,
  icalCalendarSettingsSchema,
} from '@borradh-workspace/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AddIcalSourceInput } from './ical-calendar.input';
import { buildAddIcalSourcePayload } from './ical-calendar.payload';

type CalendarCommand =
  | { type: 'enable' | 'disable' }
  | { type: 'addSource'; source: AddIcalSourceInput }
  | { type: 'removeSource' | 'refreshSource'; sourceId: string }
  | { type: 'disconnectMeet'; accountId: string };

export function useIcalCalendarSettings(person?: string, enabled = true) {
  const { data: organization } = useActiveOrganization();
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const suffix = person ? `?userId=${encodeURIComponent(person)}` : '';
  const endpoint = 'integrations/calendar/ical';
  const response = { schema: icalCalendarSettingsSchema };
  const query = useQuery({
    queryKey: queryKeys.integrations.calendarIcal(
      organization?.id,
      session?.user?.id,
      person
    ),
    enabled: enabled && Boolean(organization?.id && session?.user?.id),
    queryFn: () =>
      apiClient.get<IcalCalendarSettings>(`${endpoint}${suffix}`, response),
  });
  const mutation = useMutation({
    mutationFn: (command: CalendarCommand) => {
      switch (command.type) {
        case 'enable':
          return apiClient.post<IcalCalendarSettings>(
            `${endpoint}${suffix}`,
            {},
            response
          );
        case 'disable':
          return apiClient.delete<IcalCalendarSettings>(
            `${endpoint}${suffix}`,
            response
          );
        case 'addSource':
          return apiClient.post<IcalCalendarSettings>(
            `${endpoint}/sources${suffix}`,
            buildAddIcalSourcePayload(command.source),
            response
          );
        case 'removeSource':
          return apiClient.delete<IcalCalendarSettings>(
            `${endpoint}/sources/${command.sourceId}${suffix}`,
            response
          );
        case 'refreshSource':
          return apiClient.post<IcalCalendarSettings>(
            `${endpoint}/sources/${command.sourceId}/refresh${suffix}`,
            {},
            response
          );
        case 'disconnectMeet':
          return apiClient.delete(
            `integrations/calendar/accounts/${command.accountId}`
          );
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.integrations.calendar(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.blockedTime.all(),
        }),
      ]);
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not save calendar settings'),
  });
  return {
    ...query,
    busy: mutation.isPending,
    addingSource:
      mutation.isPending && mutation.variables?.type === 'addSource',
    enable: () => mutation.mutate({ type: 'enable' }),
    disable: () => mutation.mutate({ type: 'disable' }),
    addSource: (source: AddIcalSourceInput) =>
      mutation.mutateAsync({ type: 'addSource', source }),
    removeSource: (sourceId: string) =>
      mutation.mutate({ type: 'removeSource', sourceId }),
    refreshSource: (sourceId: string) =>
      mutation.mutate({ type: 'refreshSource', sourceId }),
    disconnectMeet: (accountId: string) =>
      mutation.mutate({ type: 'disconnectMeet', accountId }),
  };
}
